/**
 * Token CSRF delle pagine del CMS.
 *
 * Una pagina rimasta aperta oltre la durata della sessione ha il token vecchio: il "Ricordami"
 * riautentica l'utente in una sessione nuova, le letture funzionano ma ogni salvataggio viene
 * respinto con 419. Qui il token si rilegge dal server e la richiesta si ripete una volta, così
 * l'utente non vede errori e non perde quello che ha scritto.
 *
 * Non indebolisce la protezione: il token lo chiede la pagina stessa, sullo stesso dominio, e le
 * risposte del CMS non sono leggibili da altri siti.
 */

const TOKEN_MISMATCH = 419;
const SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS'];

// Tornando su una scheda rimasta in secondo piano più di così, il token si rilegge subito: copre
// anche i form inviati senza JavaScript, che una risposta 419 non potrebbe ripetere.
const IDLE_REFRESH_MS = 10 * 60 * 1000;

const nativeFetch = window.fetch.bind(window);

let refreshing = null;
let last_refresh = Date.now();
let expired_notified = false;

function isSameOrigin(url) {
	try {
		return new URL(url, window.location.href).origin === window.location.origin;
	} catch (e) {
		return false;
	}
}

function needsToken(method) {
	return !SAFE_METHODS.includes(String(method || 'GET').toUpperCase());
}

function applyToken(token) {
	window.naxosCsrfToken = token;
	$.ajaxSetup({ headers: { 'X-CSRF-TOKEN': token } });
	document.querySelectorAll('meta[name="csrf-token"]').forEach((meta) => meta.setAttribute('content', token));
	document.querySelectorAll('input[name="_token"]').forEach((input) => {
		input.value = token;
	});
	last_refresh = Date.now();
}

/**
 * Il token viaggia anche nei dati inviati, e lì Laravel lo legge prima dell'intestazione:
 * va sostituito pure lì, altrimenti il nuovo tentativo verrebbe respinto di nuovo.
 */
function withToken(data, token) {
	if (data instanceof FormData || data instanceof URLSearchParams) {
		if (data.has('_token')) {
			data.set('_token', token);
		}
		return data;
	}
	if (typeof data === 'string') {
		return data.replace(/(^|&)_token=[^&]*/, `$1_token=${encodeURIComponent(token)}`).replace(/("_token"\s*:\s*)"[^"]*"/, `$1"${token}"`);
	}
	if (data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, '_token')) {
		return { ...data, _token: token };
	}
	return data;
}

function notifySessionExpired() {
	if (expired_notified) {
		return;
	}
	expired_notified = true;
	window.toastr?.error?.('La sessione è scaduta: accedi di nuovo per salvare. Prima di ricaricare la pagina copia i testi che non hai salvato.', '', {
		timeOut: 0,
		extendedTimeOut: 0,
	});
}

/**
 * Rilegge il token della sessione corrente. Più richieste respinte insieme ne fanno una sola.
 * Fallisce se l'utente non è più autenticato: lì serve un nuovo accesso.
 */
export function refreshCsrfToken() {
	if (!refreshing) {
		refreshing = nativeFetch(window.appconfig.routes.csrf_token, {
			credentials: 'same-origin',
			cache: 'no-store',
			headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
		})
			.then((response) => (response.ok ? response.json() : null))
			.then((data) => {
				if (!data?.token) {
					throw new Error('Sessione scaduta');
				}
				applyToken(data.token);
				return data.token;
			})
			.finally(() => {
				refreshing = null;
			});
	}

	return refreshing;
}

function installJqueryRetry() {
	$.ajaxPrefilter((options, original, jqXHR) => {
		if (original.csrfRetry === false || !needsToken(options.type) || !isSameOrigin(options.url)) {
			return;
		}

		// Le callback passano dal deferred qui sotto: altrimenti scatterebbero già sul primo 419.
		const callbacks = { success: options.success, error: options.error, complete: options.complete };
		options.success = null;
		options.error = null;
		options.complete = null;

		const context = options.context || options;
		const deferred = $.Deferred();

		jqXHR.done((...args) => deferred.resolveWith(context, args));
		jqXHR.fail((xhr, ...rest) => {
			if (xhr.status !== TOKEN_MISMATCH) {
				deferred.rejectWith(context, [xhr, ...rest]);
				return;
			}

			refreshCsrfToken().then(
				(token) => {
					const retry = {
						...original,
						csrfRetry: false,
						headers: { ...original.headers, 'X-CSRF-TOKEN': token },
						data: withToken(original.data, token),
					};
					delete retry.success;
					delete retry.error;
					delete retry.complete;
					$.ajax(retry).then(
						(...args) => deferred.resolveWith(context, args),
						(...args) => deferred.rejectWith(context, args),
					);
				},
				() => {
					notifySessionExpired();
					deferred.rejectWith(context, [xhr, ...rest]);
				},
			);
		});

		deferred.done(callbacks.success || []);
		deferred.fail(callbacks.error || []);
		[].concat(callbacks.complete || []).forEach((complete) => {
			deferred.done((data, status, xhr) => complete.call(context, xhr, status));
			deferred.fail((xhr, status) => complete.call(context, xhr, status));
		});

		deferred.promise(jqXHR);
	});
}

function installFetchRetry() {
	window.fetch = async (input, init = {}) => {
		const response = await nativeFetch(input, init);
		const url = typeof input === 'string' || input instanceof URL ? String(input) : null;

		// Una Request già consumata non si può reinviare: resta la risposta originale.
		if (response.status !== TOKEN_MISMATCH || url === null || !needsToken(init.method) || !isSameOrigin(url)) {
			return response;
		}

		let token;
		try {
			token = await refreshCsrfToken();
		} catch (e) {
			notifySessionExpired();
			return response;
		}

		const headers = new Headers(init.headers || {});
		headers.set('X-CSRF-TOKEN', token);

		return nativeFetch(input, { ...init, headers, body: withToken(init.body, token) });
	};
}

function installIdleRefresh() {
	const refreshIfIdle = () => {
		if (document.visibilityState !== 'visible' || Date.now() - last_refresh < IDLE_REFRESH_MS) {
			return;
		}
		refreshCsrfToken().catch(() => notifySessionExpired());
	};

	document.addEventListener('visibilitychange', refreshIfIdle);
	window.addEventListener('focus', refreshIfIdle);
}

/**
 * Solo sulle pagine con un utente autenticato: sulle pagine di accesso non c'è sessione da rinnovare.
 */
export function initCsrfTokenRefresh() {
	if (!window.authconfig || !window.appconfig?.routes?.csrf_token || window.fetch.naxosCsrfRetry) {
		return;
	}

	installJqueryRetry();
	installFetchRetry();
	installIdleRefresh();
	window.fetch.naxosCsrfRetry = true;
}
